import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { type BlastLimitsQueryParams, type BlastQueryParams, blastLimitsService, blastService } from '~api/services';

const DEFAULT_PAGE_SIZE = 20;

/**
 * Generate infinite query options for paginated blast fetching with optional filtering parameters
 * @param config - Optional query parameters to filter blasts (mine_code, name, query)
 * @returns Query options for fetching blasts based on the provided configuration
 */
function blastsQueryOptions(config?: BlastQueryParams) {
  return infiniteQueryOptions({
    queryKey: ['blasts', 'list', 'infinite', config ?? {}],
    queryFn: ({ pageParam }) => blastService.getBlasts({ ...config, page: pageParam, page_size: DEFAULT_PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.length < DEFAULT_PAGE_SIZE ? undefined : lastPageParam + 1,
  });
}

/**
 * Generate query options for fetching a blast by its name
 * @param blastName - Name of the blast to fetch
 * @return Query options for fetching the blast by name, returning the first match or null if not found
 */
function blastByNameQueryOptions(blastName: string) {
  return queryOptions({
    queryKey: ['blasts', 'detail', blastName],
    queryFn: async () => {
      const blasts = await blastService.getBlasts({ name: blastName });
      return blasts[0] ?? null;
    },
  });
}

/**
 * Generate query options for fetching blast limits based on blast name and optional parameters
 * @param blastName - Name of the blast to fetch limits for
 * @param params - Optional query parameters (blast_id, query_buffer)
 * @returns Query options for fetching blast limits
 */
function blastLimitsQueryOptions(blastName: string, params?: BlastLimitsQueryParams) {
  return queryOptions({
    queryKey: ['blastLimits', blastName, params ?? {}],
    queryFn: () => blastLimitsService.getBlastLimits(blastName, params),
  });
}

export { blastByNameQueryOptions, blastLimitsQueryOptions, blastsQueryOptions };
